import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import { amplitudeModule } from '@/modules/amplitude';
import { ApiUserType } from '@/modules/api';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingPage } from '@/pages/Onboarding/components/Page';
import {
  OnboardingPreferencesList,
  OnboardingPreferencesOption,
  PreferencesListV1,
  PreferencesListV2,
  PreferencesListV3,
} from '@/pages/Onboarding/components/PreferencesList';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { AI_DATA } from '@/pages/Onboarding/constants';
import { useOnboarding } from '@/pages/Onboarding/context';
import { useQueryOnboardingCategories } from '@/pages/Onboarding/hooks/useQueryOnboardingCategories';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../../types';

const PreferencesStyles = styled(OnboardingPage)`
  padding: 0;

  header {
    margin: 44px 0 20px;
  }

  ion-content {
    &::part(scroll) {
      margin-top: 16px;
    }
  }
`;

export const OnboardingReplikaPreferences: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Twin Preferences',
      });
    }
    preloadImage(AI_DATA[webCampaign.aiType].noLimit.bgImageSrc);
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const categories = form.watch('categories');
  const preloadCategories = form.getValues('preloadCategories');

  const handleNextStep = () => {
    userService.createOrUpdate({
      categories,
      onboarding_data: {
        first_categories: categories,
      },
    });
    wizard.nextStep();
  };

  const handleBackStep = (): void => {
    wizard.goToStep('topics');
  };

  useQueryOnboardingCategories({
    enabled: preloadCategories.length === 0,
    form,
  });

  return (
    <PreferencesStyles>
      <OnboardingNavigation onBack={handleBackStep} />
      <OnboardingTitle color="black">
        Select your ideal AI partner
      </OnboardingTitle>
      <OnboardingSubtitle color="neutral-black">
        You can change it later in the app
      </OnboardingSubtitle>

      <IonContent>
        <PreferencesVariant
          categories={categories}
          form={form}
          preloadCategories={preloadCategories}
        />
      </IonContent>

      <OnboardingFooter
        onContinue={handleNextStep}
        disabled={categories.length === 0}
      />
    </PreferencesStyles>
  );
};

function PreferencesVariant({
  categories,
  form,
  preloadCategories,
}: {
  preloadCategories: ApiUserType['selected_categories'];
  categories: string[];
  form: UseFormReturn<LayoutDataType>;
}) {
  const variant = webCampaign.setup.preferencesVariant;

  switch (variant) {
    case 'v1':
      return (
        <PreferencesListV1
          categories={categories}
          form={form}
          preloadCategories={preloadCategories}
        />
      );
    case 'v2':
      return (
        <PreferencesListV2
          categories={categories}
          form={form}
          preloadCategories={preloadCategories}
        />
      );
    case 'v3':
      return (
        <PreferencesListV3
          categories={categories}
          form={form}
          preloadCategories={preloadCategories}
        />
      );
    default:
      return (
        <OnboardingPreferencesList>
          {preloadCategories &&
            preloadCategories.map((option) => {
              const active = categories?.includes(option.id);

              return (
                <OnboardingPreferencesOption
                  form={form}
                  active={active}
                  option={option}
                  key={option.id}
                  shape="square"
                />
              );
            })}
        </OnboardingPreferencesList>
      );
  }
}
